
/**
 * Enhanced logging utility with support for different log levels,
 * structured logging format, and performance optimizations
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogEntry {
  timestamp: string;
  level: LogLevel;
  message: string;
  context?: string;
  data?: Record<string, any>;
  sessionId?: string;
}

class Logger {
  private static instance: Logger;
  private logStorage: LogEntry[] = [];
  private maxStoredLogs = 100;
  private sessionId: string;
  private logLevel: LogLevel = 'info'; // Default log level
  private isProduction = false;
  
  // Singleton pattern
  public static getInstance(): Logger {
    if (!Logger.instance) {
      Logger.instance = new Logger();
    }
    return Logger.instance;
  }
  
  private constructor() {
    this.sessionId = this.generateSessionId();
    this.detectEnvironment();
  }
  
  private generateSessionId(): string {
    return Date.now().toString(36) + Math.random().toString(36).substr(2, 5);
  }
  
  private detectEnvironment(): void {
    // Simple environment detection
    if (typeof window !== 'undefined' && window.location && window.location.hostname) {
      const isLocalhost = window.location.hostname === 'localhost' || 
                         window.location.hostname === '127.0.0.1';
      this.isProduction = !isLocalhost;
      
      // Set appropriate log level based on environment
      this.logLevel = this.isProduction ? 'warn' : 'debug';
    }
  }
  
  /**
   * Set the minimum log level to display
   */
  public setLogLevel(level: LogLevel): void {
    this.logLevel = level;
  }
  
  /**
   * Check if the given log level should be processed
   */
  private shouldLog(level: LogLevel): boolean {
    const levels: Record<LogLevel, number> = {
      debug: 0,
      info: 1,
      warn: 2,
      error: 3
    };
    
    return levels[level] >= levels[this.logLevel];
  }
  
  /**
   * Creates a structured log entry
   */
  private createLogEntry(
    level: LogLevel,
    message: string,
    context?: string,
    data?: Record<string, any>
  ): LogEntry {
    // Skip if below current log level
    if (!this.shouldLog(level)) {
      return {
        timestamp: '',
        level,
        message,
        context,
        data,
        sessionId: this.sessionId
      };
    }
    
    const logEntry = {
      timestamp: new Date().toISOString(),
      level,
      message,
      context,
      data: this.sanitizeData(data),
      sessionId: this.sessionId
    };
    
    // Store logs in memory (limited to prevent memory leaks)
    this.logStorage.unshift(logEntry);
    if (this.logStorage.length > this.maxStoredLogs) {
      this.logStorage.pop();
    }
    
    return logEntry;
  }
  
  /**
   * Sanitize sensitive data before logging
   */
  private sanitizeData(data: Record<string, any> | undefined): Record<string, any> | undefined {
    if (!data) return undefined;
    
    const sanitized = { ...data };
    
    // Redact common sensitive fields
    const sensitiveKeys = ['password', 'token', 'secret', 'key', 'auth', 'credential'];
    
    Object.keys(sanitized).forEach(key => {
      const lowerKey = key.toLowerCase();
      
      // Check if the key contains any sensitive terms
      if (sensitiveKeys.some(term => lowerKey.includes(term))) {
        sanitized[key] = '[REDACTED]';
      }
      
      // Recursively sanitize nested objects
      if (sanitized[key] && typeof sanitized[key] === 'object' && !Array.isArray(sanitized[key])) {
        sanitized[key] = this.sanitizeData(sanitized[key]);
      }
    });
    
    return sanitized;
  }
  
  /**
   * Format and output log entry to console
   */
  private outputLog(entry: LogEntry): void {
    // Skip if below current log level
    if (!this.shouldLog(entry.level)) return;
    
    const formattedData = entry.data ? JSON.stringify(entry.data, null, 2) : '';
    const contextStr = entry.context ? `[${entry.context}]` : '';
    const sessionStr = this.isProduction ? '' : `[${entry.sessionId.substring(0, 4)}]`;
    
    // Select appropriate console method based on level
    switch (entry.level) {
      case 'debug':
        console.debug(`🔍 DEBUG ${sessionStr} ${contextStr} ${entry.timestamp}: ${entry.message}`, entry.data || '');
        break;
      case 'info':
        console.info(`ℹ️ INFO ${sessionStr} ${contextStr} ${entry.timestamp}: ${entry.message}`, entry.data || '');
        break;
      case 'warn':
        console.warn(`⚠️ WARN ${sessionStr} ${contextStr} ${entry.timestamp}: ${entry.message}`, entry.data || '');
        break;
      case 'error':
        console.error(`❌ ERROR ${sessionStr} ${contextStr} ${entry.timestamp}: ${entry.message}`, entry.data || '');
        break;
      default:
        console.log(`LOG ${sessionStr} ${contextStr} ${entry.timestamp}: ${entry.message}`, entry.data || '');
    }
    
    // In production, we could send logs to a backend service
    if (this.isProduction && (entry.level === 'error' || entry.level === 'warn')) {
      this.sendLogToServer(entry);
    }
  }
  
  /**
   * Send critical logs to a backend service
   * Note: Implement this when a backend logging service is available
   */
  private sendLogToServer(entry: LogEntry): void {
    // This is a placeholder for sending logs to a backend service
    // In a real implementation, this would use fetch or similar to send logs
    try {
      if (typeof window !== 'undefined') {
        // Example implementation (commented out as it's not connected to a real endpoint)
        /*
        fetch('/api/logs', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(entry),
        });
        */
      }
    } catch (e) {
      // Silently fail to avoid infinite error loops
      console.error('Failed to send log to server', e);
    }
  }
  
  /**
   * Log a debug message
   */
  public debug(message: string, context?: string, data?: Record<string, any>): void {
    const entry = this.createLogEntry('debug', message, context, data);
    this.outputLog(entry);
  }
  
  /**
   * Log an info message
   */
  public info(message: string, context?: string, data?: Record<string, any>): void {
    const entry = this.createLogEntry('info', message, context, data);
    this.outputLog(entry);
  }
  
  /**
   * Log a warning message
   */
  public warn(message: string, context?: string, data?: Record<string, any>): void {
    const entry = this.createLogEntry('warn', message, context, data);
    this.outputLog(entry);
  }
  
  /**
   * Log an error message
   */
  public error(message: string, context?: string, data?: Record<string, any>): void {
    const entry = this.createLogEntry('error', message, context, data);
    this.outputLog(entry);
  }
  
  /**
   * Get all stored logs (for debugging purposes)
   */
  public getLogs(): LogEntry[] {
    return [...this.logStorage];
  }
  
  /**
   * Get logs filtered by level and/or context
   */
  public getFilteredLogs(level?: LogLevel, context?: string): LogEntry[] {
    return this.logStorage.filter(log => 
      (!level || log.level === level) && 
      (!context || log.context === context)
    );
  }
  
  /**
   * Clear stored logs
   */
  public clearLogs(): void {
    this.logStorage = [];
  }
  
  /**
   * Track performance with timing information
   */
  public trackPerformance(operationName: string, context?: string): () => void {
    const startTime = performance.now();
    return () => {
      const duration = performance.now() - startTime;
      this.info(`Performance: ${operationName} took ${duration.toFixed(2)}ms`, context, { 
        operation: operationName,
        durationMs: duration
      });
    };
  }
}

// Export singleton instance
export const logger = Logger.getInstance();

// For convenience, export log level functions directly
export const logDebug = (message: string, context?: string, data?: Record<string, any>) => 
  logger.debug(message, context, data);
export const logInfo = (message: string, context?: string, data?: Record<string, any>) => 
  logger.info(message, context, data);
export const logWarn = (message: string, context?: string, data?: Record<string, any>) => 
  logger.warn(message, context, data);
export const logError = (message: string, context?: string, data?: Record<string, any>) => 
  logger.error(message, context, data);

// Export performance tracking utility
export const trackPerformance = (operationName: string, context?: string) => 
  logger.trackPerformance(operationName, context);

// Utility to log all errors from promises
export const logPromiseErrors = (promise: Promise<any>, context: string) => {
  return promise.catch(error => {
    logError("Unhandled promise error", context, {
      error: error instanceof Error ? error.message : String(error),
      stack: error instanceof Error ? error.stack : undefined
    });
    throw error;
  });
};

// Create enhanced console wrapper to catch all logs
export const enhancedConsole = {
  log: (message: any, ...args: any[]) => {
    console.log(message, ...args);
    logInfo(String(message), "console", { args });
  },
  warn: (message: any, ...args: any[]) => {
    console.warn(message, ...args);
    logWarn(String(message), "console", { args });
  },
  error: (message: any, ...args: any[]) => {
    console.error(message, ...args);
    logError(String(message), "console", { args });
  }
};
